import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import moment from 'moment';
import queryString from 'query-string';
import {
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router';
import {
  LoginActions,
  Authentication,
  color,
  StorageUtil,
  VibeButton,
} from 'vibeguide';
import { passwordValidation } from '../../utils/validation';
import { markPageLoading } from '../../actions/loading-actions';
import LoginForm from './LoginForm';
import ForgotPasswordForm from './ForgotPasswordForm';
import ResetPasswordForm from './ResetPasswordForm';
import FirstTimeLoginForm from './FirstTimeLoginForm';
import LoadingSpinner from '../Shared/LoadingSpinner';
import {
  getDiscoverErrorMessage,
  getRedirectErrorMessage,
  SSO_ERROR_STORAGE_KEY,
} from './ssoErrors';
import logoImage from '../../assets/ism-logo.png';
// Required for dev environment to export the favicon
// import '../../assets/favicon.png';
// import '../../assets/favicon-32x32.png';
import '../../assets/favicon-180x180.png';
import '../../assets/favicon-192x192.png';
import './LoginContainer.scss';

function LoginContainer({
  path = null,
  authenticated = false,
  user,
  loading = false,
  resetSuccess = false,
  markPageLoading,
  doResetPasswordSuccess,
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginStatus, setLoginStatus] = useState('email');
  const [newPassword, setNewPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [loginError, setLoginError] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState(false);
  const [forgotPasswordErrorMessage, setForgotPasswordErrorMessage] = useState('');
  const [confirmationCodeError, setConfirmationCodeError] = useState(false);
  const [confirmationCodeErrorMessage, setConfirmationCodeErrorMessage] = useState('');
  const [passwordErrors, setPasswordErrors] = useState({
    number: true,
    uppercase: true,
    lowercase: true,
    special: true,
    minLength: true,
    errors: true,
  });
  const [challengeData, setChallengeData] = useState(null);
  const [cookies, setCookies] = useState(StorageUtil.getLocal('cookies', { accepted: false }));
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginRoute = path === '/login' || path === null;

  const showLoginError = (message) => {
    setLoginStatus('email');
    setLoginError(true);
    setLoginErrorMessage(message);
  };

  const usernameChange = (e) => {
    setUsername(e.target.value.trim());
  };

  const passwordChange = (e) => {
    setPassword(e.target.value.trim());
  };

  const onChangeEmail = () => {
    setLoginStatus('email');
    setPassword('');
    setLoginError(false);
    setLoginErrorMessage('');
  };

  const onClickAcceptCookies = () => {
    const data = {
      accepted: true,
      date: moment().format(),
    };

    StorageUtil.setLocal('cookies', data, moment().add(1, 'year').format());
    setCookies(data);
  };

  const passwordValidateChange = (e) => {
    const password = e.target.value;
    const passwordErrors = passwordValidation(password, password, 15);
    setPasswordErrors(passwordErrors);
    setPassword(password);
  };

  const passwordValidateChangeFirstTime = (e) => {
    const newPassword = e.target.value;
    const passwordErrors = passwordValidation(newPassword, newPassword, 15);
    setPasswordErrors(passwordErrors);
    setNewPassword(newPassword);
  };

  const resendActivationCode = () => {
    Authentication.forgotPassword(username);
  };

  const onDiscoverEmail = async (e) => {
    e.preventDefault();
    setLoginError(false);
    setLoginErrorMessage('');

    try {
      markPageLoading(true);
      const { source, redirectUrl } = await Authentication.discoverIdentitySource(username);

      if (source === Authentication.IDENTITY_SOURCE_FEDERATED) {
        const clientId = Authentication.extractClientIdFromRedirectUrl(redirectUrl);
        Authentication.setSsoClientId(clientId);
        window.location.assign(redirectUrl);
        return;
      }

      setLoginStatus('password');
      setPassword('');
    } catch (err) {
      showLoginError(getDiscoverErrorMessage(err));
    }

    markPageLoading(false);
  };

  const checkCredentials = async (e) => {
    e.preventDefault();
    const Username = username;
    const Password = password;
    const authenticationData = {
      Username,
      Password,
    };

    try {
      markPageLoading(true);
      setLoginError(false);
      const authenticated = await Authentication.authenticateUser(authenticationData);
      if (authenticated?.type === 'AUTHENTICATION.CHALLENGED'
        && authenticated.data.challengeType === 'newPasswordRequired')
      {
        setLoginStatus('firstTimeLogin');
        setChallengeData(authenticated);
      }
    } catch (err) {
      const errorMessage = err?.message || err;

      setLoginError(true);
      setLoginErrorMessage(errorMessage);
    }

    markPageLoading(false);
  };

  const firstTimeChangePassword = (e) => {
    markPageLoading(true);

    Authentication.acceptChallenge(challengeData, newPassword)
      .then(() => {
        doResetPasswordSuccess(true);
        markPageLoading(false);
      })
      .catch(error => {
        markPageLoading(false);
        throw new Error(error);
      });

    e.preventDefault();
  };

  const sendActivationCode = () => {
    markPageLoading(true);

    Authentication.forgotPassword(username)
      .then(() => {
        setLoginStatus('resetPassword');
        setPassword('');

        markPageLoading(false);
      })
      .catch(() => {
        const forgotPasswordErrorMessage = 'Username Not Found';
        setForgotPasswordError(true);
        setForgotPasswordErrorMessage(forgotPasswordErrorMessage);

        markPageLoading(false);
      });
  };

  const resetForgottenPassword = (e) => {
    const Username = username;
    const Password = password;
    const authenticationDetails = {
      Username,
      Password,
    };

    markPageLoading(true);

    Authentication.confirmResetPassword(username, confirmationCode, password)
      .then(() => {
        Authentication.authenticateUser(authenticationDetails);
      })
      .then(() => {
        doResetPasswordSuccess(true);
        markPageLoading(false);
      })
      .catch(() => {
        const confirmationCodeErrorMessage = 'Incorrect Activation Code';
        setConfirmationCodeError(true);
        setConfirmationCodeErrorMessage(confirmationCodeErrorMessage);

        markPageLoading(false);
      });

    e.preventDefault();
  };

  // LoginContainer is reused across /login, /forgot, and /reset, so local
  // state survives browser back. Statuses like 'forgotPassword' have no
  // login-route renderer and would otherwise leave a blank screen (VW-7720).
  useEffect(() => {
    if (!isLoginRoute) {
      return;
    }

    if (
      loginStatus === 'email'
      || loginStatus === 'password'
      || loginStatus === 'firstTimeLogin'
    ) {
      return;
    }

    setLoginStatus(username ? 'password' : 'email');
  }, [isLoginRoute, loginStatus, username]);

  useEffect(() => {
    if (!isLoginRoute) {
      return;
    }

    const {
      error,
      error_description: errorDescription,
    } = queryString.parse(location.search);

    // Cognito returns failed federated sign-in as ?error=&error_description= on the login URL.
    if (error || errorDescription) {
      const message = getRedirectErrorMessage({ error, errorDescription });
      sessionStorage.setItem(SSO_ERROR_STORAGE_KEY, message);
      showLoginError(message);
      navigate({
        pathname: location.pathname,
        search: '',
      }, { replace: true });
      return;
    }

    const storedError = sessionStorage.getItem(SSO_ERROR_STORAGE_KEY);

    if (storedError) {
      sessionStorage.removeItem(SSO_ERROR_STORAGE_KEY);
      showLoginError(storedError);
    }
  }, [isLoginRoute, location.pathname, location.search, navigate]);

  const isResetPage = path === '/reset';
  const hasLoginFormStatus = loginStatus === 'email'
    || loginStatus === 'password'
    || loginStatus === 'firstTimeLogin';
  const loginStep = isLoginRoute && !hasLoginFormStatus
    ? (username ? 'password' : 'email')
    : loginStatus;
  const isFirstTimeLogin = loginStep === 'firstTimeLogin';
  const isEmailStep = loginStep === 'email';
  const isPasswordStep = loginStep === 'password';

  if (authenticated && user?._id) {
    return (
      <Navigate
        to={user.getRootUrl()}
        replace
      />
    );
  }

  if (resetSuccess && isResetPage) {
    // user successfully reset their password
    return (
      <Navigate
        to={{
          pathname: '/',
        }}
      />
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: color.primary,
      }}
    >
      {!cookies.accepted && (
        <div
          style={{
            position: 'absolute',
            top: 100,
            left: 0,
            width: '100%',
            height: '100%',
            paddingTop: 32,
            backdropFilter: 'blur(6px)',
            textAlign: 'center',
            color: color.primary,
            zIndex: 2,
          }}
        />
      )}
      <div className={classNames('LoginContainer', { 'reset-page': isResetPage, 'no-cookies': !cookies.accepted })}>
        {loading && <LoadingSpinner />}

        <div className="login-content">
          <div className="logo-container">
            <img
              className="logo"
              src={logoImage}
              alt="In-Store Marketplace"
            />
          </div>

          {isLoginRoute && (
            <div>
              {isEmailStep && (
                <LoginForm
                  step="email"
                  onUsernameChange={usernameChange}
                  onSubmit={onDiscoverEmail}
                  username={username}
                  loginError={loginError}
                  loginErrorMessage={loginErrorMessage}
                  cookiesAccepted={cookies.accepted}
                />
              )}

              {isPasswordStep && (
                <LoginForm
                  step="password"
                  onUsernameChange={usernameChange}
                  onPasswordChange={passwordChange}
                  onChangeEmail={onChangeEmail}
                  onSubmit={checkCredentials}
                  username={username}
                  password={password}
                  loginError={loginError}
                  loginErrorMessage={loginErrorMessage}
                  cookiesAccepted={cookies.accepted}
                  emailReadOnly
                  passwordAutoFocus
                />
              )}

              {isFirstTimeLogin && (
                <FirstTimeLoginForm
                  passwordValidateChange={passwordValidateChangeFirstTime}
                  onFirstTimeChangePassword={firstTimeChangePassword}
                  errors={passwordErrors}
                />
              )}
            </div>
          )}

          {path === '/forgot' && (
            <ForgotPasswordForm
              sendActivationCode={sendActivationCode}
              onUsernameChange={usernameChange}
              username={username}
              forgotPasswordError={forgotPasswordError}
              forgotPasswordErrorMessage={forgotPasswordErrorMessage}
            />
          )}

          {path === '/reset' && (
            <ResetPasswordForm
              passwordValidateChange={passwordValidateChange}
              resetPassword={resetForgottenPassword}
              resendActivationCode={resendActivationCode}
              onConfirmationCodeChange={(e) => setConfirmationCode(e.target.value.trim())}
              errors={passwordErrors}
              confirmationCode={confirmationCode}
              confirmationCodeError={confirmationCodeError}
              confirmationCodeErrorMessage={confirmationCodeErrorMessage}
            />
          )}
        </div>
      </div>

      {!cookies.accepted && (
        <div className="accept-cookies-container">
          <div className="cookie-information">
            <div className="accept-cookies-title">
              Accept our necessary cookies.
            </div>
            <div className="accept-cookies-message">
              We use cookies to optimize our website. Further information can be found in our
              Terms of Service/Privacy Policy.
            </div>
          </div>
          <div className="accept-cookies-btn">
            <VibeButton
              style={{
                height: 52,
                fontSize: 18,
                fontWeight: 700,
                minWidth: 268,
              }}
              text="Accept Necessary Cookies"
              color="primary"
              testId="cookies-accept-button"
              onClick={onClickAcceptCookies}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function mapStateToProps(state) {
  return {
    authenticated: state.login.authenticated,
    user: state.login.user,
    loading: state.loading.pageLoading,
    resetSuccess: state.login.resetSuccess,
  };
}

const mapDispatchToProps = {
  markPageLoading,
  doResetPasswordSuccess: LoginActions.doResetPasswordSuccess,
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginContainer);
